import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {ColumnDef} from "@tanstack/react-table";
import {DataTable} from "@/lib/components/DataTable";
import {CollaborationByMonthDTO} from "@/api/data-contracts";
import {ReportI18N} from "@/translations";

interface CollaborationByMonthReportProps {
    data: CollaborationByMonthDTO[];
}

interface CollaborationByMonthRow extends CollaborationByMonthDTO {
    id: number;
}

/**
 * Report table showing collaboration counts per requester-supplier pair per calendar month.
 */
const CollaborationByMonthReport: React.FC<CollaborationByMonthReportProps> = ({ data }) => {
    const { t } = useTranslation();

    const rows = useMemo<CollaborationByMonthRow[]>(
        () => data.map((item, index) => ({ ...item, id: index })),
        [data]
    );

    const columns = useMemo<ColumnDef<CollaborationByMonthRow>[]>(
        () => [
            {
                header: t(ReportI18N.page.column.year),
                accessorFn: (row) => row.year ?? "",
                id: "year"
            },
            {
                header: t(ReportI18N.page.column.month),
                accessorFn: (row) => row.month ?? "",
                id: "month"
            },
            {
                header: t(ReportI18N.page.column.requester),
                accessorFn: (row) => row.requesterCompanyName ?? "",
                id: "requesterCompanyName"
            },
            {
                header: t(ReportI18N.page.column.supplier),
                accessorFn: (row) => row.supplierCompanyName ?? "",
                id: "supplierCompanyName"
            },
            {
                header: t(ReportI18N.page.column.count),
                accessorFn: (row) => row.quoteCount ?? 0,
                id: "quoteCount"
            }
        ],
        [t]
    );

    return <DataTable columns={columns} data={rows} noResultsValue="" striped maxHeight={940} />;
};

export default CollaborationByMonthReport;
